
import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface BusinessFeature {
  id: string;
  title: string;
  description: string;
  iconName?: string;
}

export interface LandingSettings {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  backgroundColor: string;
  textColor: string;
  heroTitle: string;
  heroSubtitle: string;
  featureTitle: string;
  features: BusinessFeature[];
  ctaTitle: string;
  ctaText: string;
  ctaButtonText: string;
  footerText: string;
}

interface LandingContextType {
  settings: LandingSettings;
  isLoading: boolean;
  businessName: string;
}

export const LandingContext = createContext<LandingContextType>({
  settings: {
    primaryColor: '',
    secondaryColor: '',
    accentColor: '',
    backgroundColor: '',
    textColor: '',
    heroTitle: '',
    heroSubtitle: '',
    featureTitle: '',
    features: [],
    ctaTitle: '',
    ctaText: '',
    ctaButtonText: '',
    footerText: ''
  },
  isLoading: false,
  businessName: ''
});

export const useLandingContext = () => useContext(LandingContext);

interface LandingProviderProps {
  children: ReactNode;
  settings?: LandingSettings;
  isLoading?: boolean;
  businessName?: string;
}

export const LandingProvider = ({ 
  children, 
  settings = {
    primaryColor: '#4F46E5',
    secondaryColor: '#818CF8',
    accentColor: '#F97316',
    backgroundColor: '#F9FAFB',
    textColor: '#1F2937',
    heroTitle: 'Grow Your Business with Referrals',
    heroSubtitle: 'Our referral program helps you acquire new customers through word-of-mouth marketing',
    featureTitle: 'Why Choose Our Referral Program',
    features: [
      {
        id: '1',
        title: 'Easy to Share',
        description: 'Share your referral link with just a few clicks',
        iconName: 'Share'
      },
      {
        id: '2',
        title: 'Track Performance',
        description: 'Monitor your referrals and earnings in real-time',
        iconName: 'Activity'
      },
      {
        id: '3',
        title: 'Earn Rewards',
        description: 'Get rewarded for every successful referral',
        iconName: 'Gift'
      }
    ],
    ctaTitle: 'Ready to Start Earning?',
    ctaText: 'Join our referral program today and start earning rewards',
    ctaButtonText: 'Sign Up Now',
    footerText: '© 2025 Referral Program. All rights reserved.'
  }, 
  isLoading = false, 
  businessName = 'Our Business' 
}: LandingProviderProps) => {
  
  return (
    <LandingContext.Provider value={{ settings, isLoading, businessName }}>
      {children}
    </LandingContext.Provider>
  );
};
